import { useState } from "react";
import { handleAuthExpired } from "../app/views/auth/auth";
import {spendCleaningService} from "../services/spendCleaningService";
const ENV_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim();
const IS_PRODUCTION = process.env.NODE_ENV === "production";

const API_BASE_CANDIDATES = (
  IS_PRODUCTION
    ? [ENV_API_BASE_URL]
    : [ENV_API_BASE_URL, "http://localhost:5001", "http://127.0.0.1:5001"]
).filter((url, index, arr) => arr.indexOf(url) === index);

let ACTIVE_API_BASE_URL = API_BASE_CANDIDATES[0] || "";

const buildNetworkError = (path: string, attempts: string[]) => {
  const attempted = attempts.join(", ");

  return new Error(
    `Unable to reach backend for ${path}. Tried: ${attempted}. ` +
      `Start backend with 'npm run server' or set REACT_APP_API_BASE_URL correctly.`
  );
};

const requestWithBaseFallback = async (
  path: string,
  options: RequestInit = {}
) => {
  const candidateList = [ACTIVE_API_BASE_URL, ...API_BASE_CANDIDATES].filter(
    (url, index, arr) => arr.indexOf(url) === index
  );

  const requestOptions: RequestInit = {
    credentials: "include",
    ...options,
  };

  const attempted: string[] = [];
  let lastNetworkError: unknown = null;

  for (const baseUrl of candidateList) {
    const finalUrl = `${baseUrl}${path}`;
    attempted.push(baseUrl);

    try {
      const res = await fetch(finalUrl, requestOptions);

      if (!res.ok) {
        const details = await res.text().catch(() => "");

        if (res.status === 401) {
          handleAuthExpired();
          throw new Error("Unauthorized: please log in again.");
        }

        throw new Error(
          `${path} failed with ${res.status} ${res.statusText}${
            details ? `: ${details}` : ""
          }`
        );
      }

      ACTIVE_API_BASE_URL = baseUrl;
      return res;
    } catch (err) {
      if (err instanceof TypeError) {
        lastNetworkError = err;
        continue;
      }

      throw err;
    }
  }

  const fallbackError = buildNetworkError(path, attempted);

  if (lastNetworkError) {
    (fallbackError as any).cause = lastNetworkError;
  }

  throw fallbackError;
};

export const useOpenAICleaning = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runOpenAICleaning = async (rows: Record<string, unknown>[]) => {
    setLoading(true);
    setError(null);

    try {
      console.log("🚀 STARTING OPENAI CLEANING...");
      console.log("📦 OPENAI CLEANING INPUT SAMPLE:", {
        rowCount: rows?.length || 0,
        columns: rows?.[0] ? Object.keys(rows[0]) : [],
        firstRow: rows?.[0] || null,
      });

      const res = await requestWithBaseFallback("/openai/clean-data", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          rows,
        }),
      });

      const data = await res.json();
      const saveresponse = await spendCleaningService.saveDataCleaningRecord({
        original_data: rows,
        cleaned_data: data,
        model_name: "Genie-Team-GPT-5.4",
        status: "SUCCESS",
        });
      console.log("✅ OPENAI CLEANING SAVE RESPONSE IN DATABASE:", saveresponse);
      console.log("✅ OPENAI CLEANING RESPONSE:", data);

      setLoading(false);
      return data;
    } catch (err: any) {
      console.error("❌ OPENAI CLEANING ERROR:", err);

      setError(err?.message || "OpenAI cleaning failed");
      setLoading(false);

      throw err;
    }
  };

  return {
    runOpenAICleaning,
    loading,
    error,
  };
};

export default useOpenAICleaning;